import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Users, Plus, Lock, ShieldCheck, UserCog, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { generateSalt, createProbe, deriveLabKey, cacheLabKey } from "@/lib/crypto";

export default function Labs() {
  const navigate = useNavigate();
  const [labs, setLabs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", passphrase: "", confirm: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/labs");
    setLabs(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Lab name required");
    if (form.passphrase.length < 8) return toast.error("Passphrase must be at least 8 characters");
    if (form.passphrase !== form.confirm) return toast.error("Passphrases do not match");
    setCreating(true);
    try {
      const salt = await generateSalt();
      const probe = await createProbe(form.passphrase, salt);
      const { data: lab } = await api.post("/labs", {
        name: form.name,
        description: form.description,
        probe,
      });
      // Cache the derived key so the new lab opens unlocked
      const key = await deriveLabKey(form.passphrase, salt);
      cacheLabKey(lab.id, key);
      toast.success("Lab created. Share the passphrase with members via a secure channel.");
      setOpen(false);
      setForm({ name: "", description: "", passphrase: "", confirm: "" });
      navigate(`/labs/${lab.id}`);
    } catch (e) {
      toast.error("Failed to create lab");
    } finally { setCreating(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-6 animate-fade-in" data-testid="labs-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">Collaboration</div>
          <h1 className="text-3xl sm:text-4xl font-sans font-bold tracking-tight mt-1">Research labs</h1>
          <p className="text-sm font-body text-zinc-500 mt-1 max-w-2xl">
            Private, end-to-end encrypted workspaces. Supervisors add members, and the group shares
            updates encrypted with a shared passphrase that only members know.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-lab-btn">
              <Plus className="h-4 w-4 mr-2" /> New lab
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create a research lab</DialogTitle>
              <DialogDescription>
                You become the supervisor. Messages are end-to-end encrypted with a passphrase you set —
                LabTracker never sees it. Share it with your team via a secure channel.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Field label="Lab name">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Chen Lab – Genomics" data-testid="lab-name-input" />
              </Field>
              <Field label="Description (optional)">
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Field>
              <Field label="Shared passphrase">
                <Input type="password" value={form.passphrase} onChange={(e) => setForm({ ...form, passphrase: e.target.value })} placeholder="At least 8 characters" data-testid="lab-passphrase-input" />
              </Field>
              <Field label="Confirm passphrase">
                <Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="lab-passphrase-confirm" />
              </Field>
              <div className="flex items-start gap-2 text-xs font-mono text-zinc-500 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded p-3">
                <Lock className="h-3.5 w-3.5 mt-0.5 text-amber-600" />
                <span>
                  If you lose this passphrase, encrypted messages cannot be recovered. ResyNote cannot reset it.
                </span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-lab-btn">
                {creating ? "Creating…" : "Create lab"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {labs.length === 0 ? (
        <div className="border border-dashed border-zinc-300 dark:border-zinc-700 rounded-md p-16 text-center">
          <Users className="h-8 w-8 mx-auto text-zinc-400 mb-3" />
          <div className="font-sans font-semibold">No labs yet</div>
          <div className="text-sm font-body text-zinc-500 mt-1 max-w-md mx-auto">
            Create a lab to collaborate with your supervisor or team. Everything is encrypted end-to-end.
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {labs.map((lab) => (
            <Link
              to={`/labs/${lab.id}`}
              key={lab.id}
              className="border border-zinc-200 dark:border-zinc-800 rounded-md bg-white dark:bg-zinc-900 p-5 hover:border-blue-500 transition-colors group"
              data-testid={`lab-card-${lab.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-sans font-semibold text-lg truncate group-hover:text-blue-600 transition-colors">{lab.name}</h3>
                  {lab.description && (
                    <p className="text-sm font-body text-zinc-600 dark:text-zinc-400 mt-1 line-clamp-2">{lab.description}</p>
                  )}
                </div>
                {lab.role === "supervisor" ? (
                  <span title="Supervisor" className="text-[10px] font-mono uppercase tracking-widest text-blue-600 border border-blue-200 dark:border-blue-900/60 bg-blue-50 dark:bg-blue-950/40 rounded px-1.5 py-0.5 flex items-center gap-1">
                    <UserCog className="h-3 w-3" /> sup
                  </span>
                ) : (
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-zinc-200 dark:border-zinc-800 rounded px-1.5 py-0.5">member</span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3 text-xs font-mono text-zinc-500">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                end-to-end encrypted
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
